import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Download, 
  Trash2, 
  Plus, 
  FolderOpen,
  File,
  FileCheck,
  Search,
  ArrowLeft,
  Upload,
  MoreVertical,
  Building2,
  Lightbulb,
  Landmark,
  Receipt,
  Home,
  Users,
  Handshake,
  Briefcase,
  Scale,
  Shield,
  Palette,
  Sprout,
  Folder
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';
import { formatErrorDetail } from '../../utils/formatError';

// Icon mapping for folders
const FOLDER_ICONS = {
  company: Building2,
  ip: Lightbulb,
  banking: Landmark,
  tax: Receipt,
  office: Home,
  hr: Users,
  vendors: Handshake,
  customers: Briefcase,
  legal: Scale,
  insurance: Shield,
  marketing: Palette,
  spring: Sprout,
  general: Folder
};

const Documents = () => {
  const { token } = useAuth();
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [totalDocs, setTotalDocs] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [newDoc, setNewDoc] = useState({ 
    name: '', 
    doc_type: 'doc', 
    category: 'working',
    folder: 'general'
  });

  useEffect(() => {
    fetchFolders();
  }, []);

  useEffect(() => {
    if (selectedFolder) {
      fetchDocuments(selectedFolder.id);
    }
  }, [selectedFolder]);

  const fetchFolders = async () => {
    try {
      const response = await axios.get(`${API}/documents/folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFolders(response.data.folders);
      setTotalDocs(response.data.total_documents);
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
    setLoading(false);
  };

  const fetchDocuments = async (folderId) => {
    try {
      const response = await axios.get(`${API}/documents?folder=${folderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data.documents);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const addDocument = async () => {
    if (!newDoc.name.trim()) return;

    try {
      const docData = {
        ...newDoc,
        folder: selectedFolder?.id || newDoc.folder
      };
      await axios.post(
        `${API}/documents`,
        docData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewDoc({ name: '', doc_type: 'doc', category: 'working', folder: 'general' });
      setShowAddDoc(false);
      if (selectedFolder) {
        fetchDocuments(selectedFolder.id);
      }
      fetchFolders();
      toast.success('Document added');
    } catch (error) {
      toast.error('Could not add document');
    }
  };

  const deleteDocument = async (docId) => {
    try {
      await axios.delete(`${API}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (selectedFolder) {
        fetchDocuments(selectedFolder.id);
      }
      fetchFolders();
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Could not delete document');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10MB.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const folderId = selectedFolder?.id || 'general';
      const res = await axios.post(
        `${API}/documents/upload?folder=${folderId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success(`Uploaded: ${res.data.document.name}`);
      if (selectedFolder) fetchDocuments(selectedFolder.id);
      fetchFolders();
    } catch (error) {
      const detail = formatErrorDetail(error.response?.data?.detail) || 'Upload failed';
      toast.error(detail);
    }
    setUploading(false);
    e.target.value = '';
  };

  const getDocIcon = (docType) => {
    switch (docType) {
      case 'cert': return <FileCheck className="h-5 w-5 text-[#F6ADBC]" />;
      case 'pdf': return <FileText className="h-5 w-5 text-red-500" />;
      default: return <File className="h-5 w-5 text-blue-500" />;
    }
  };

  const filteredFolders = folders.filter(folder => 
    folder.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    folder.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredDocs = documents.filter(doc => 
    doc.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Folder View
  if (!selectedFolder) {
    return (
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl headline text-black mb-2">Documents</h1>
            <p className="text-black/60 body-text">
              All your business documents organised in one place.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-secondary px-4 py-2 rounded-none">
              <span className="text-sm text-black/60">{totalDocs} documents</span>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black/40" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search folders..."
            className="pl-10 bg-white border-border rounded-none"
            data-testid="search-folders"
          />
        </div>

        {/* Folders Grid */}
        {loading ? (
          <div className="text-center py-8 text-black/40">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredFolders.map((folder) => {
              const IconComponent = FOLDER_ICONS[folder.id] || Folder;
              return (
                <button
                  key={folder.id}
                  onClick={() => setSelectedFolder(folder)}
                  className="bg-white rounded-none p-5 border border-border hover:border-primary/30 hover:shadow-sm transition-all text-left group"
                  data-testid={`folder-${folder.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-none bg-secondary flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                      <IconComponent className="h-6 w-6 text-black/60 group-hover:text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="subhead text-black truncate">{folder.name}</h3>
                        {folder.count > 0 && (
                          <span className="text-xs bg-primary/20 text-black px-2 py-0.5 rounded-none ml-2">
                            {folder.count}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-black/50 mt-1 line-clamp-2">
                        {folder.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Help Section */}
        <div className="bg-secondary/50 rounded-none p-6">
          <h3 className="subhead text-black mb-2">Keep your business organised</h3>
          <p className="text-sm text-black/60">
            Store all your important documents here — from incorporation papers to vendor agreements. 
            Everything in one place, easy to find when you need it.
          </p>
        </div>
      </div>
    );
  }

  // Document List View (inside a folder)
  const IconComponent = FOLDER_ICONS[selectedFolder.id] || Folder;
  
  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => {
            setSelectedFolder(null);
            setDocuments([]);
            setSearchQuery('');
          }}
          className="p-2 hover:bg-secondary rounded-none transition-colors"
          data-testid="back-to-folders"
        >
          <ArrowLeft className="h-5 w-5 text-black/60" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-none bg-primary/10 flex items-center justify-center">
              <IconComponent className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl headline text-black">{selectedFolder.name}</h1>
              <p className="text-sm text-black/50">{selectedFolder.description}</p>
            </div>
          </div>
        </div>
        <Button
          onClick={() => setShowAddDoc(true)}
          className="bg-primary text-white hover:bg-primary/90 rounded-none"
          data-testid="add-document-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Document
        </Button>
        <label className={`inline-flex items-center gap-2 px-4 py-2 bg-[#1E491A] text-white hover:bg-[#333] rounded-none cursor-pointer transition-colors text-sm font-medium ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
          <Upload className="h-4 w-4" />
          {uploading ? 'Uploading...' : 'Upload File'}
          <input
            type="file"
            className="hidden"
            onChange={handleFileUpload}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.pptx,.csv,.txt,.jpg,.jpeg,.png,.webp"
            disabled={uploading}
            data-testid="file-upload-input"
          />
        </label>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black/40" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search documents..."
          className="pl-10 bg-white border-border rounded-none"
          data-testid="search-documents"
        />
      </div>

      {/* Add Document Form */}
      {showAddDoc && (
        <div className="bg-white rounded-none p-5 border border-border">
          <h3 className="subhead text-black mb-4">Add Document to {selectedFolder.name}</h3>
          <div className="space-y-4">
            <Input
              value={newDoc.name}
              onChange={(e) => setNewDoc({ ...newDoc, name: e.target.value })}
              placeholder="Document name..."
              className="bg-secondary border-0 rounded-none"
              data-testid="new-doc-name"
            />
            <div className="flex gap-4">
              <select
                value={newDoc.doc_type}
                onChange={(e) => setNewDoc({ ...newDoc, doc_type: e.target.value })}
                className="flex-1 px-4 py-2 bg-secondary border-0 rounded-none text-black"
              >
                <option value="doc">Document</option>
                <option value="pdf">PDF</option>
                <option value="cert">Certificate</option>
                <option value="image">Image</option>
                <option value="spreadsheet">Spreadsheet</option>
              </select>
              <select
                value={newDoc.category}
                onChange={(e) => setNewDoc({ ...newDoc, category: e.target.value })}
                className="flex-1 px-4 py-2 bg-secondary border-0 rounded-none text-black"
              >
                <option value="working">Working Draft</option>
                <option value="final">Final Version</option>
                <option value="template">Template</option>
              </select>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={addDocument}
                className="bg-primary text-white hover:bg-primary/90 rounded-none"
              >
                Add Document
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowAddDoc(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Document List */}
      {filteredDocs.length > 0 ? (
        <div className="bg-white rounded-none border border-border overflow-hidden">
          <div className="divide-y divide-border">
            {filteredDocs.map((doc) => (
              <DocumentRow 
                key={doc.id} 
                doc={doc} 
                getDocIcon={getDocIcon}
                onDelete={deleteDocument}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-none p-8 border border-border text-center">
          <FolderOpen className="h-12 w-12 text-black/20 mx-auto mb-3" />
          <p className="text-black/40 mb-2">No documents yet</p>
          <p className="text-sm text-black/30">
            Add your first document to this folder.
          </p>
          {selectedFolder.examples && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs text-black/40 mb-2">Suggested documents:</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {selectedFolder.examples.slice(0, 4).map((example, idx) => (
                  <span key={idx} className="text-xs bg-secondary px-3 py-1 rounded-none text-black/60">
                    {example}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Folder Info */}
      {selectedFolder.examples && filteredDocs.length > 0 && (
        <div className="bg-secondary/50 rounded-none p-4">
          <p className="text-xs text-black/40 mb-2">Common documents in this folder:</p>
          <div className="flex flex-wrap gap-2">
            {selectedFolder.examples.map((example, idx) => (
              <span key={idx} className="text-xs bg-white px-3 py-1 rounded-none text-black/60">
                {example}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DocumentRow = ({ doc, getDocIcon, onDelete }) => {
  const { token } = useAuth();
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-NZ', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const categoryLabel = {
    working: 'Draft',
    final: 'Final',
    template: 'Template'
  };

  const handleView = () => {
    if (doc.content && doc.type === 'html') {
      const win = window.open('', '_blank');
      win.document.write(doc.content);
      win.document.close();
    } else if (doc.url) {
      window.open(doc.url, '_blank');
    }
  };

  const handleDownloadPdf = async () => {
    if (!doc.download_url) return;
    try {
      const response = await axios.get(`${API}${doc.download_url.replace('/api', '')}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.name || 'report.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch (error) {
      toast.error('Could not download PDF');
    }
  };

  return (
    <div className="flex items-center gap-4 p-4 hover:bg-secondary/30 transition-colors">
      {getDocIcon(doc.doc_type)}
      <div className="flex-1 min-w-0">
        <p className="text-black truncate">{doc.name}</p>
        <div className="flex items-center gap-3 mt-1 text-xs text-black/40">
          {doc.category && (
            <span className={`px-2 py-0.5 rounded-none ${
              doc.category === 'final' ? 'bg-green-100 text-[#1E491A]' :
              doc.category === 'template' ? 'bg-blue-100 text-blue-700' :
              'bg-secondary text-black/60'
            }`}>
              {categoryLabel[doc.category] || doc.category}
            </span>
          )}
          {doc.created_at && <span>{formatDate(doc.created_at)}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {/* View button for HTML reports */}
        {doc.content && doc.type === 'html' && (
          <button
            onClick={handleView}
            className="p-2 text-black/40 hover:text-primary rounded-none"
            title="View Report"
            data-testid={`view-doc-${doc.id}`}
          >
            <FileText className="h-4 w-4" />
          </button>
        )}
        {/* Download button for PDFs */}
        {doc.download_url && doc.doc_type === 'pdf' && (
          <button
            onClick={handleDownloadPdf}
            className="p-2 text-black/40 hover:text-primary rounded-none"
            title="Download PDF"
            data-testid={`download-doc-${doc.id}`}
          >
            <Download className="h-4 w-4" />
          </button>
        )}
        {/* External URL link / uploaded file download */}
        {doc.url && !doc.download_url && (
          <a
            href={doc.url.startsWith('/uploads/') ? `${API.replace('/api','')}${doc.url}` : doc.url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-black/40 hover:text-primary rounded-none"
            data-testid={`open-doc-${doc.id}`}
          >
            <Download className="h-4 w-4" />
          </a>
        )}
        <button
          onClick={() => onDelete(doc.id)}
          className="p-2 text-black/30 hover:text-red-500 rounded-none"
          data-testid={`delete-doc-${doc.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default Documents;
